import React from 'react';
import { Plane } from 'lucide-react';

interface FlightData {
  flightNumber: string;
  airline: string;
  status: string;
  departure: {
    airport: string;
    city: string;
    terminal?: string;
    gate?: string;
    scheduledTime: string;
    actualTime?: string;
    timezone?: string; // Added timezone for departure
  };
  arrival: {
    airport: string;
    city: string;
    terminal?: string;
    gate?: string;
    scheduledTime: string;
    estimatedTime?: string;
    actualTime?: string;
    timezone?: string; // Added timezone for arrival
  };
  duration?: string;
  aircraft?: string;
}

interface FlightStatusWidgetProps {
  flightData: FlightData;
}

export function FlightStatusWidget({ flightData }: FlightStatusWidgetProps) {
  const getStatusStyles = (status: string) => {
    const statusMap: Record<string, string> = {
      'on-time': '#34a853',
      'scheduled': '#34a853',
      'boarding': '#fa7b17',
      'departed': '#1a73e8',
      'in-flight': '#1a73e8',
      'landed': '#34a853',
      'cancelled': '#ea4335',
      'delayed': '#fbbc04'
    };
    return statusMap[status.toLowerCase()] || '#34a853';
  };

  const calculateDelay = (scheduled: string, actual: string) => {
    if (!scheduled || !actual) return '';
    const scheduledTime = new Date(scheduled).getTime();
    const actualTime = new Date(actual).getTime();
    const diff = actualTime - scheduledTime;
    const minutes = Math.abs(Math.floor(diff / 60000));
    
    if (minutes === 0) return '(on time)';
    // return diff > 0 ? `(${minutes} minutes late)` : `(${minutes} minutes early)`;
    return diff > 0 ? `${minutes} minutes late` : `${minutes} minutes early`;
  };

  const formatTime = (time: string, timezone?: string) => {
    if (!time) return '--:--';
    
    // Parse time directly from string to avoid timezone conversion
    const parts = time.split(/[T\s]/);
    const timePart = parts[1]?.split(/[+-]/)[0];
    if (!timePart) return '--:--';
    
    const [hourStr, minuteStr] = timePart.split(':');
    let hours = parseInt(hourStr);
    const minutes = parseInt(minuteStr);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    
    const timeStr = `${hours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    
    // Timezone mapping
    const tzMap: Record<string, string> = {
      'Europe/Berlin': 'CEST',
      'Europe/London': 'BST',
      'Asia/Hong_Kong': 'HKT',
      'Asia/Tokyo': 'JST',
      'Asia/Seoul': 'KST',
      'Asia/Dubai': 'GST',
      'America/Chicago': 'CDT',
      'America/Panama': 'EST'
    };
    
    const tzDisplay = timezone ? tzMap[timezone] || '' : '';
    return tzDisplay ? `${timeStr} ${tzDisplay}` : timeStr;
  };

  const formatDate = (time: string) => {
    if (!time) return '';
    const date = new Date(time);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Calculate flight progress
  const calculateFlightProgress = () => {
    const now = new Date();
    const hasActuallyDeparted = !!flightData.departure.actualTime;
    const departure = new Date(flightData.departure.actualTime || flightData.departure.scheduledTime);
    const arrival = new Date(flightData.arrival.estimatedTime || flightData.arrival.scheduledTime);
    
    const totalDuration = arrival.getTime() - departure.getTime();
    const elapsed = now.getTime() - departure.getTime();
    
    let progress = 0;
    let actualElapsed = 0;
    let actualRemaining = totalDuration;
    
    if (!hasActuallyDeparted) {
      // Flight hasn't departed yet
      progress = 0;
      actualElapsed = 0;
      actualRemaining = totalDuration;
    } else if (now < departure) {
      // Flight departed but somehow current time is before departure (edge case)
      progress = 0;
      actualElapsed = 0;
      actualRemaining = totalDuration;
    } else if (now > arrival) {
      // Flight has landed
      progress = 100;
      actualElapsed = totalDuration;
      actualRemaining = 0;
    } else {
      // Flight is in progress
      progress = (elapsed / totalDuration) * 100;
      actualElapsed = elapsed;
      actualRemaining = totalDuration - elapsed;
    }
    
    const elapsedHours = Math.floor(Math.abs(actualElapsed) / (1000 * 60 * 60));
    const elapsedMinutes = Math.floor((Math.abs(actualElapsed) % (1000 * 60 * 60)) / (1000 * 60));
    const totalHours = Math.floor(Math.abs(totalDuration) / (1000 * 60 * 60));
    const totalMinutes = Math.floor((Math.abs(totalDuration) % (1000 * 60 * 60)) / (1000 * 60));
    const remainingHours = Math.floor(actualRemaining / (1000 * 60 * 60));
    const remainingMinutes = Math.floor((actualRemaining % (1000 * 60 * 60)) / (1000 * 60));
    
    return {
      progress: Math.max(0, Math.min(100, progress)),
      elapsed: elapsedHours >= 1 ? `${elapsedHours}h ${elapsedMinutes}m` : `${elapsedMinutes}m`,
      total: totalHours >= 1 ? `${totalHours}h ${totalMinutes}m` : `${totalMinutes}m`,
      remaining: remainingHours >= 1 ? `${remainingHours}h ${remainingMinutes}m` : `${remainingMinutes}m`,
      elapsedDisplay: elapsedHours >= 1 ? `${elapsedHours}h ${elapsedMinutes}m elapsed` : `${elapsedMinutes}m elapsed`,
      remainingDisplay: remainingHours >= 1 ? `Reaching in ${remainingHours}h ${remainingMinutes}m` : `Reaching in ${remainingMinutes}m`
    };
  };

  const flightProgress = calculateFlightProgress();
  const statusText = flightData.status.toUpperCase().replace('-', ' ');
  const statusColor = getStatusStyles(flightData.status);

  return (
    <div 
      /* style={{ 
        backgroundColor: '#ffffff',
        borderRadius: '18px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',//'0 10px 3px rgba(0,0,0,0.24), 10 4px 3px rgba(0,0,0,0.24)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        overflow: 'hidden',
        marginTop: '12px',
        width: '100%',
        margin: '5px auto 0',
        maxWidth: 'min(var(--chat-panel-width, 500px), 98%)',
        padding: 'var(--flight-widget-padding)'
      }} */
     style={{ 
        backgroundColor: '#ffffff',
        borderRadius: '18px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
        overflow: 'hidden',
        margin: '12px auto 0',
        width: 'calc(var(--chat-panel-width) * 0.93)',
        maxWidth: '98%'
      }}
    >
      {/* Header */}
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '12px 16px',
        borderBottom: '1px solid #e8eaed',
        borderTopLeftRadius: '18px',
        borderTopRightRadius: '18px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          {/* <div style={{
            fontSize: 'var(--auth-font-size-base)',
            fontWeight: '500',
            color: '#202124',
            marginBottom: '2px'
          }}>
            {formatTime(flightData.departure.scheduledTime, flightData.departure.timezone)}
          </div> */}
          <div style={{
            fontSize: '16px',
            fontWeight: '500',
            color: '#202124'
          }}>
            {flightData.airline} {flightData.flightNumber}
          </div>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          {/* <span style={{
            fontSize: '13px',
            color: '#5f6368'
          }}>
            to {flightData.arrival.city}
          </span> */}
          <span style={{
            fontSize: 'var(--auth-font-size-xs)',
            fontWeight: '500',
            color: statusColor,
            backgroundColor: `${statusColor}15`,
            padding: '4px 8px',
            borderRadius: '12px',
            border: `1px solid ${statusColor}`
          }}>
            {statusText}
          </span>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '16px' }}>

        {/* Airport Names and Date */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '4px'
      }}>
        <div style={{
          fontSize: '12px',
          color: '#5f6368'
        }}>
          {formatDate(flightData.departure.scheduledTime)}
        </div>
        <div style={{
          fontSize: '12px',
          color: '#5f6368',
          textAlign: 'right'
        }}>
          {formatDate(flightData.arrival.scheduledTime)}
        </div>
      </div>

        {/* Airport Codes */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#202124'
          }}>
            {flightData.departure.airport}
          </div>
          
          <div style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#202124'
          }}>
            {flightData.arrival.airport}
          </div>
        </div>

        {/* Airport Names and Date */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '4px'
        }}>
          <div style={{
            fontSize: '12px',
            color: '#5f6368'
          }}>
            {flightData.departure.city}
          </div>
          <div style={{
            fontSize: '12px',
            color: '#5f6368',
            textAlign: 'right'
          }}>
            {flightData.arrival.city}
          </div>
        </div>

        {/* Airport Links */}
       {/*  <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '24px'
        }}>
          <a href="#" style={{
            fontSize: 'var(--auth-font-size-xs)',
            color: '#1a73e8',
            textDecoration: 'underline'
          }}>
            Airport info
          </a>
          <a href="#" style={{
            fontSize: 'var(--auth-font-size-xs)',
            color: '#1a73e8',
            textDecoration: 'underline'
          }}>
            Airport info
          </a>
        </div> */}

        {/* Flight Progress Bar */}
        <div style={{ marginBottom: '12px' }}>
          <div style={{
            position: 'relative',
            height: '40px',
            marginBottom: '8px'
          }}>
            {/* Progress Line */}
            <div style={{
              position: 'absolute',
              top: '19px',
              left: '0',
              right: '0',
              height: '2px',
              backgroundColor: '#e8eaed'
            }}>
              {/* Filled Progress */}
              <div style={{
                position: 'absolute',
                left: '0',
                top: '0',
                height: '100%',
                width: `${flightProgress.progress}%`,
                backgroundColor: statusColor,
                transition: 'width 0.3s ease'
              }} />
            </div>
            
            {/* Start Dot */}
            <div style={{
              position: 'absolute',
              left: '-4px',
              top: '15px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: statusColor,
              border: '2px solid #ffffff',
              boxShadow: '0 0 0 1px ' + statusColor
            }} />
            
            {/* End Dot */}
            <div style={{
              position: 'absolute',
              right: '-4px',
              top: '15px',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              backgroundColor: flightProgress.progress >= 100 ? statusColor : '#e8eaed',
              border: '2px solid #ffffff',
              boxShadow: '0 0 0 1px ' + (flightProgress.progress >= 100 ? statusColor : '#e8eaed')
            }} />
            
            {/* Plane Icon */}
            <div style={{
              position: 'absolute',
              left: `${flightProgress.progress}%`,
              top: '10px',
              transform: 'translateX(-50%)',
              transition: 'left 0.3s ease'
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill={statusColor} style={{ transform: 'rotate(90deg)' }}>
                <path d="M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z"/>
              </svg>
            </div>
          </div>
          
          {/* Progress Times */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: 'var(--auth-font-size-sm)',
            color: '#5f6368'
          }}>
            {/* <span>{flightProgress.elapsedDisplay}</span>
            <span>{flightProgress.total} total travel time</span> */}
            <span style={{ textAlign: 'right' }}>{flightProgress.remainingDisplay}</span>
          </div>
        </div>

        {/* Departure and Arrival Details */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '1px'
        }}>
          {/* Departure */}
          <div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{
                fontSize: 'var(--auth-font-size-xs)',
                color: '#9aa0a6',
                marginBottom: '2px'
              }}>
                {flightData.departure.actualTime ? 'Departed' : 'Scheduled'}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: statusColor
              }}>
                {formatTime(flightData.departure.actualTime || flightData.departure.scheduledTime, flightData.departure.timezone)}
              </div>
              {flightData.departure.actualTime && (
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#5f6368',
                  marginTop: '2px'
                }}>
                  {calculateDelay(flightData.departure.scheduledTime, flightData.departure.actualTime)}
                </div>
              )}
              {/* {flightData.departure.actualTime && flightData.departure.actualTime !== flightData.departure.scheduledTime && (
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  textDecoration: 'line-through'
                }}>
                  {formatTime(flightData.departure.scheduledTime)}
                </div>
              )} */}
            </div>
            
            {/* <div style={{
              display: 'flex',
              gap: '16px'
            }}>
              <div>
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  marginBottom: '2px'
                }}>Terminal</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#202124'
                }}>{flightData.departure.terminal || '-'}</div>
              </div>
              <div>
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  marginBottom: '2px'
                }}>Gate</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#202124'
                }}>{flightData.departure.gate || '-'}</div>
              </div>
            </div> */}
          </div>

          {/* Arrival */}
          <div style={{ textAlign: 'right' }}>
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: 'var(--auth-font-size-xs)',
                color: '#9aa0a6',
                marginBottom: '2px'
              }}>
                {flightData.arrival.actualTime ? 'Arrived' : 'Scheduled arrival'}
              </div>
              <div style={{
                fontSize: '16px',
                fontWeight: '500',
                color: statusColor
              }}>
                {formatTime(flightData.arrival.estimatedTime || flightData.arrival.scheduledTime, flightData.arrival.timezone)}
              </div>
              {flightData.arrival.estimatedTime && flightData.arrival.scheduledTime && (
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#5f6368',
                  marginTop: '2px'
                }}>
                  {calculateDelay(flightData.arrival.scheduledTime, flightData.arrival.estimatedTime)}
                </div>
              )}
              {/* {flightData.arrival.estimatedTime && flightData.arrival.estimatedTime !== flightData.arrival.scheduledTime && (
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  textDecoration: 'line-through'
                }}>
                  {formatTime(flightData.arrival.scheduledTime, flightData.arrival.timezone)}
                </div>
              )} */}
            </div>
            
            {/* <div style={{
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end'
            }}>
              <div>
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  marginBottom: '2px'
                }}>Terminal</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#202124'
                }}>{flightData.arrival.terminal || '-'}</div>
              </div>
              <div>
                <div style={{
                  fontSize: 'var(--auth-font-size-xs)',
                  color: '#9aa0a6',
                  marginBottom: '2px'
                }}>Gate</div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '500',
                  color: '#202124'
                }}>{flightData.arrival.gate || '-'}</div>
              </div>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
}

// Demo component with sample data
export default function FlightStatusDemo() {
  const flightData = {
    "flightNumber": "NH184",
    "airline": "All Nippon Airways",
    "status": "in-flight",
    "departure": {
        "airport": "NRT",
        "city": "Narita",
        "terminal": "1",
        "gate": "46",
        "scheduledTime": "2025-06-23T20:10:00+00:00",
        "actualTime": "2025-06-23T20:29:00+00:00"
    },
    "arrival": {
        "airport": "HNL",
        "city": "Honolulu International",
        "terminal": "2",
        "gate": "",
        "scheduledTime": "2025-06-23T08:30:00+00:00",
        "estimatedTime": "2025-06-23T08:30:00+00:00"
    },
    "duration": "-12h -40m",
    "aircraft": "Unknown"
  };

  return <FlightStatusWidget flightData={flightData} />;
}